import {
  DatePipe,
  NgClass,
  NgTemplateOutlet,
  SlicePipe,
} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  finalize,
  forkJoin,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';

import { ContactService } from 'src/app/core/services/contact';
import { User } from 'src/app/models/user.model';
import { ToastNotificationComponent } from '../../shared/components/toast-notification/toast-notification.component';
import {
  closeDropdown,
  closeDropdownOnOutsideClick,
  stopEvent,
  toggleDropdown,
} from '../../shared/utils/dropdown.util';
import * as fromStore from '../../store';
import {
  nonPublicEmailValidator,
  PUBLIC_EMAIL_DOMAIN_ERROR,
} from '../../shared/validators/nonPublicEmailValidator';
import { OktaUserService } from '../services/okta-user';
import type {
  AssignToGroupResponse,
  FederationCheckData,
  MakeFederatedResponse,
  OktaUserExistsData,
  OktaUserListItem,
  OktaUserRequest,
  OktaUserResponse,
  OktaUserStatus,
} from '../services/okta-user/okta-user.types';
import {
  isFresh,
  OktaUsersActions,
  oktaUsersFeature,
  OktaUsersState,
  pageKey,
  RequestStatus,
} from '../store';

interface StatusConfig {
  label: string;
  classes: string;
  dot: string;
}

const STATUS_CONFIG: Record<OktaUserStatus, StatusConfig> = {
  ACTIVE: {
    label: 'Active',
    classes: 'bg-green-100 text-green-700',
    dot: 'bg-green-500',
  },
  LOCKED_OUT: {
    label: 'Locked Out',
    classes: 'bg-red-100 text-red-700',
    dot: 'bg-red-500',
  },
  SUSPENDED: {
    label: 'Suspended',
    classes: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
  },
  PROVISIONED: {
    label: 'Provisioned',
    classes: 'bg-blue-100 text-blue-700',
    dot: 'bg-blue-700',
  },
  STAGED: {
    label: 'Staged',
    classes: 'bg-gray-100 text-gray-600',
    dot: 'bg-gray-400',
  },
  RECOVERY: {
    label: 'Recovery',
    classes: 'bg-purple-100 text-purple-700',
    dot: 'bg-purple-300',
  },
  PASSWORD_EXPIRED: {
    label: 'Password Expired',
    classes: 'bg-orange-100 text-orange-700',
    dot: 'bg-orange-500',
  },
  DEPROVISIONED: {
    label: 'Deprovisioned',
    classes: 'bg-gray-100 text-gray-500',
    dot: 'bg-gray-300',
  },
};

@Component({
  selector: 'app-okta-admin',
  templateUrl: 'okta-admin.component.html',
  imports: [
    DatePipe,
    FormsModule,
    MatIconModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    NgClass,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SlicePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OktaAdminComponent implements OnInit, AfterViewInit, OnDestroy {
  // --- Form ---
  private readonly fb = inject(FormBuilder);

  private readonly contactService = inject(ContactService);

  readonly publicEmailDomainError = PUBLIC_EMAIL_DOMAIN_ERROR;

  readonly onboardingForm = this.fb.group({
    username: ['', Validators.required],
    email: [
      '',
      [Validators.required, Validators.email, nonPublicEmailValidator()],
    ],
  });

  readonly isSubmitting = signal(false);

  readonly formError = signal<string | null>(null);

  readonly formSuccess = signal<string | null>(null);

  // --- Federation check form ---
  readonly federationForm = this.fb.group({
    username: ['', Validators.required],
  });

  readonly isChecking = signal(false);

  readonly federationResult = signal<FederationCheckData | null>(null);

  readonly federationError = signal<string | null>(null);
  /** is-federated response fields, shown as returned by the API. */
  readonly federationCheckFields: readonly (keyof FederationCheckData)[] = [
    'username',
    'federated',
    'domainFederated',
    'existsInOkta',
    'idpName',
    'userFederated',
    'inGroup',
    'diagnosis',
  ];

  readonly existsResult = signal<OktaUserExistsData | null>(null);
  /** Looked-up user in table-row shape, so the table's details and actions can be reused. */
  private readonly lookupUserBase = signal<OktaUserListItem | null>(null);
  private readonly mfaCache = signal<OktaUsersState['mfaByUserId']>({});
  readonly lookupUser = computed(() => {
    const user = this.lookupUserBase();
    if (!user) return null;
    const cached = this.mfaCache()[user.id];
    // MFA only comes from the fresh cache, so a reset or invalidation never
    // leaves stale factors from a table-row snapshot.
    return {
      ...user,
      mfa: isFresh(cached, Date.now()) ? cached.mfa : undefined,
    };
  });

  readonly existsError = signal<string | null>(null);

  // --- Assign to group ---
  readonly isAssigningToGroup = signal(false);

  readonly assignToGroupResult = signal<AssignToGroupResponse | null>(null);

  readonly assignToGroupError = signal<string | null>(null);

  // --- Make federated form ---
  readonly makeFederatedForm = this.fb.group({
    username: ['', Validators.required],
  });

  readonly isMakingFederated = signal(false);

  readonly makeFederatedResult = signal<MakeFederatedResponse | null>(null);

  readonly makeFederatedError = signal<string | null>(null);

  // --- Delete user ---
  readonly isDeleting = signal(false);

  readonly deleteConfirmPending = signal(false);

  readonly deleteError = signal<string | null>(null);

  readonly deleteSuccess = signal<string | null>(null);

  private readonly store = inject(Store<fromStore.State>);

  readonly loggedInUser = signal<User>({} as User);

  // --- Table ---
  @ViewChild(MatSort) sort!: MatSort;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private readonly oktaUserService = inject(OktaUserService);

  private readonly snackBar = inject(MatSnackBar);

  private readonly elementRef = inject(ElementRef);

  private readonly destroy$ = new Subject<void>();

  readonly isLoading = signal(true);

  readonly errorMessage = signal<string | null>(null);

  /** True when the last loaded page has a nextCursor. */
  readonly hasMore = signal(false);

  readonly isLoadingMore = signal(false);
  /** User ids with a branded activation email request in flight. */
  readonly resendingCustomActivationIds = signal<ReadonlySet<string>>(
    new Set(),
  );

  readonly loadMoreError = signal<string | null>(null);

  readonly mfaStatus = signal<Record<string, RequestStatus>>({});

  /**
   * Cursors of the pages shown in the table, in order ([null] = first page
   * only). Pages are appended one at a time via "Load more"; we never follow
   * nextCursor automatically, to avoid bursts of Okta calls.
   */
  private readonly loadedCursors$ = new BehaviorSubject<(string | null)[]>([
    null,
  ]);

  private nextCursor: string | null = null;

  readonly statusConfig = STATUS_CONFIG;

  readonly statusOptions = Object.entries(STATUS_CONFIG) as [
    OktaUserStatus,
    StatusConfig,
  ][];

  readonly dataSource = new MatTableDataSource<OktaUserListItem>([]);

  readonly displayedColumns = [
    'avatar',
    'user',
    'email',
    'login',
    'status',
    'authProvider',
    'created',
    'lastLogin',
    'statusChanged',
    'passwordChanged',
    'mfa',
    'actions',
  ];

  readonly pageSizeOptions = [50, 100, 200];

  readonly defaultPageSize = 100;

  searchTerm = '';

  statusFilter: OktaUserStatus[] = [];

  authProviderFilter = signal<'OKTA' | 'FEDERATION' | ''>('');

  isStatusFilterOpen = signal(false);

  searchField: 'all' | 'login' | 'email' | 'name' = 'all';

  ngOnInit(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const { search, statuses, field, authProvider } = JSON.parse(filter) as {
        search: string;
        statuses: OktaUserStatus[];
        field: 'all' | 'login' | 'email' | 'name';
        authProvider: 'OKTA' | 'FEDERATION' | '';
      };
      const term = search.toLowerCase();
      let matchesSearch = true;
      if (term) {
        switch (field) {
          case 'login':
            matchesSearch = !!data.login?.toLowerCase().includes(term);
            break;
          case 'email':
            matchesSearch = !!data.email?.toLowerCase().includes(term);
            break;
          case 'name':
            matchesSearch =
              !!data.firstName?.toLowerCase().includes(term) ||
              !!data.lastName?.toLowerCase().includes(term);
            break;
          default:
            matchesSearch =
              !!data.login?.toLowerCase().includes(term) ||
              !!data.firstName?.toLowerCase().includes(term) ||
              !!data.lastName?.toLowerCase().includes(term) ||
              !!data.email?.toLowerCase().includes(term) ||
              !!data.loginIssue?.reason?.toLowerCase().includes(term);
        }
      }
      const matchesStatus = !statuses.length || statuses.includes(data.status);
      const matchesAuth = !authProvider || data.authProvider === authProvider;
      return matchesSearch && matchesStatus && matchesAuth;
    };

    this.store
      .select(fromStore.getCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => this.loggedInUser.set(user));

    combineLatest([
      this.loadedCursors$,
      this.store.select(oktaUsersFeature.selectOktaUsersState),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([cursors, state]) => this.syncTable(cursors, state));

    this.loadUsers();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sortingDataAccessor = (user, column) => {
      switch (column) {
        case 'user':
          return `${user.lastName} ${user.firstName}`.toLowerCase();
        case 'lastLogin':
          return user.lastLogin ?? '';
        case 'statusChanged':
          return user.statusChanged ?? '';
        case 'passwordChanged':
          return user.passwordChanged ?? '';
        case 'mfa':
          if (!user.mfa) return '2';
          if (!user.mfa.enrolled) return '1';
          return `0 ${user.mfa.factors
            ?.map((factor) => `${factor.provider} ${factor.type}`)
            .join(' ')}`;
        default:
          return (
            ((user as unknown as Record<string, unknown>)[column] as string) ??
            ''
          );
      }
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    closeDropdownOnOutsideClick(
      this.elementRef,
      event,
      this.isStatusFilterOpen,
    );
  }

  // --- Form actions ---
  onAssignToGroup(): void {
    const username = this.federationResult()?.username;
    if (!username || this.isAssigningToGroup()) return;

    this.isAssigningToGroup.set(true);
    this.assignToGroupResult.set(null);
    this.assignToGroupError.set(null);

    this.oktaUserService
      .assignToGroup(username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.assignToGroupResult.set(res);
          this.isAssigningToGroup.set(false);
          this.lookUpUser(username);
        },
        error: (err) => {
          this.assignToGroupError.set(
            err?.message || 'Failed to assign user to group.',
          );
          this.isAssigningToGroup.set(false);
        },
      });
  }

  onLookUpUser(): void {
    const username = this.federationForm.value.username?.trim().toLowerCase();
    if (username) this.lookUpUser(username);
  }

  /**
   * Refreshes take the affected user's login rather than the form input, which
   * may have been cleared or edited since the lookup.
   */
  private lookUpUser(username: string): void {
    if (this.isChecking()) return;

    this.isChecking.set(true);
    this.federationResult.set(null);
    this.federationError.set(null);
    this.existsResult.set(null);
    this.existsError.set(null);
    this.lookupUserBase.set(null);

    forkJoin({
      federation: this.oktaUserService.checkFederation(username).pipe(
        tap({
          error: (err) =>
            this.federationError.set(
              err?.message || 'Failed to check federation status.',
            ),
        }),
        catchError(() => of(null)),
      ),
      exists: this.oktaUserService.checkExists(username).pipe(
        tap({
          error: (err) =>
            this.existsError.set(
              err?.message || 'Failed to check if user exists.',
            ),
        }),
        catchError(() => of(null)),
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ federation, exists }) => {
          if (federation) this.federationResult.set(federation.data);
          if (exists) {
            this.existsResult.set(exists.data);
            this.lookupUserBase.set(this.toLookupUser(exists.data));
          }
          this.isChecking.set(false);
        },
        error: () => this.isChecking.set(false),
      });
  }

  onMakeFederated(): void {
    const { username } = this.makeFederatedForm.value;
    if (!username || this.isMakingFederated()) return;

    this.isMakingFederated.set(true);
    this.makeFederatedResult.set(null);
    this.makeFederatedError.set(null);

    this.oktaUserService
      .makeFederated(username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.makeFederatedResult.set(res);
          this.isMakingFederated.set(false);
        },
        error: (err) => {
          this.makeFederatedError.set(
            err?.message || 'Failed to convert user to federated.',
          );
          this.isMakingFederated.set(false);
        },
      });
  }

  onDeleteUser(): void {
    this.deleteConfirmPending.set(true);
    this.deleteError.set(null);
    this.deleteSuccess.set(null);
  }

  cancelDelete(): void {
    this.deleteConfirmPending.set(false);
  }

  onConfirmDelete(): void {
    const { username } = this.makeFederatedForm.value;
    if (!username || this.isDeleting()) return;

    this.isDeleting.set(true);
    this.deleteError.set(null);

    this.oktaUserService
      .deleteUser(username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deleteSuccess.set(`"${username}" has been deleted from Okta.`);
          this.isDeleting.set(false);
          this.deleteConfirmPending.set(false);
          this.makeFederatedForm.reset();
          this.reloadUsers();
        },
        error: (err: { message?: string }) => {
          this.deleteError.set(err?.message || 'Failed to delete user.');
          this.isDeleting.set(false);
          this.deleteConfirmPending.set(false);
        },
      });
  }

  onSubmit(): void {
    this.formError.set(null);
    this.formSuccess.set(null);
    if (this.onboardingForm.invalid || this.isSubmitting()) return;
    this.isSubmitting.set(true);

    const email = this.onboardingForm.value.email?.trim().toLowerCase();
    const username = this.onboardingForm.value.username?.trim().toLowerCase();

    this.contactService
      .getContactByOktaUsernameAndEmail(email, username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contact) => {
          if (!contact) {
            this.formError.set(
              `No Salesforce/Hyperdrive contact or Okta user found for ${email} / ${username}.`,
            );
            this.isSubmitting.set(false);
            return;
          }

          const payload: OktaUserRequest = {
            email: contact.Email,
            login: contact.OktaUsername__c,
            firstName: contact.FirstName,
            lastName: contact.LastName,
            accountId: contact.AccountId,
          };

          this.oktaUserService
            .onboardUser(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.formSuccess.set(
                  'User successfully created. A default Okta activation email should be received shortly.',
                );
                this.isSubmitting.set(false);
                this.onboardingForm.reset();
                this.reloadUsers();
              },
              error: (err) => {
                this.isSubmitting.set(false);
                this.formError.set(`Failed to create user. ${err?.message}`);
              },
            });
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.formError.set(
            (err?.message as string) ||
              'An error occurred looking up the contact.',
          );
        },
      });
  }

  onSubmitCustomActivationEmail(): void {
    this.formError.set(null);
    this.formSuccess.set(null);
    if (this.onboardingForm.invalid || this.isSubmitting()) return;
    this.isSubmitting.set(true);

    const email = this.onboardingForm.value.email?.trim().toLowerCase();
    const username = this.onboardingForm.value.username?.trim().toLowerCase();

    this.contactService
      .getContactByOktaUsernameAndEmail(email, username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contact) => {
          if (!contact) {
            this.formError.set(
              `No Salesforce/Hyperdrive contact or Okta user found for ${email} / ${username}.`,
            );
            this.isSubmitting.set(false);
            return;
          }

          const payload: OktaUserRequest = {
            email: contact.Email,
            login: contact.OktaUsername__c,
            firstName: contact.FirstName,
            lastName: contact.LastName,
            accountId: contact.AccountId,
          };

          this.oktaUserService
            .onboardUserWithCustomActivationEmail(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res) => {
                this.formSuccess.set(
                  this.getCustomActivationEmailSuccessMessage(res),
                );
                this.isSubmitting.set(false);
                this.onboardingForm.reset();
                this.reloadUsers();
              },
              error: (err) => {
                this.isSubmitting.set(false);
                this.formError.set(`Failed to create user. ${err?.message}`);
              },
            });
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.formError.set(
            (err?.message as string) ||
              'An error occurred looking up the contact.',
          );
        },
      });
  }

  private getCustomActivationEmailSuccessMessage(
    res: OktaUserResponse,
  ): string {
    const apiMessage = res.message?.trim();
    const data = res.status === 'ok' ? res.data : undefined;
    const isFederated = this.isFederatedOnboardingResponse(data);
    const emailWasNotSent = data?.customActivationEmailSent === false;
    const userWasAddedToGroup = res.code.toUpperCase().includes('GROUP');

    if (apiMessage && (isFederated || emailWasNotSent || userWasAddedToGroup)) {
      return apiMessage;
    }

    return 'User successfully created. A custom activation email should be received shortly.';
  }

  private isFederatedOnboardingResponse(data: unknown): boolean {
    if (!data || typeof data !== 'object') return false;

    const { authProvider } = data as { authProvider?: unknown };

    return (
      typeof authProvider === 'string' &&
      authProvider.toUpperCase().includes('FEDERATION')
    );
  }

  // --- Table filter ---

  applyFilter(): void {
    this.dataSource.filter = JSON.stringify({
      search: this.searchTerm.trim(),
      statuses: this.statusFilter,
      field: this.searchField,
      authProvider: this.authProviderFilter(),
    });
    this.dataSource.paginator?.firstPage();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilter();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.statusFilter = [];
    this.searchField = 'all';
    this.authProviderFilter.set('');
    this.applyFilter();
  }

  toggleStatusFilterDropdown(event: Event): void {
    stopEvent(event);
    toggleDropdown(this.isStatusFilterOpen);
  }

  toggleStatusFilter(status: OktaUserStatus, event: Event): void {
    stopEvent(event);
    this.statusFilter = this.isStatusSelected(status)
      ? this.statusFilter.filter((selectedStatus) => selectedStatus !== status)
      : [...this.statusFilter, status];
    this.applyFilter();
  }

  clearStatusFilter(event: Event): void {
    stopEvent(event);
    this.statusFilter = [];
    closeDropdown(this.isStatusFilterOpen);
    this.applyFilter();
  }

  isStatusSelected(status: OktaUserStatus): boolean {
    return this.statusFilter.includes(status);
  }

  get selectedStatusFilterLabel(): string {
    if (!this.statusFilter.length) return 'All statuses';
    if (this.statusFilter.length === 1) {
      return this.getStatusConfig(this.statusFilter[0]).label;
    }
    return `${this.statusFilter.length} statuses`;
  }

  // --- Table helpers ---

  getStatusConfig(status: OktaUserStatus): StatusConfig {
    return (
      this.statusConfig[status] ?? {
        label: status,
        classes: 'bg-gray-100 text-gray-600',
        dot: 'bg-gray-400',
      }
    );
  }

  getMfaTooltip(user: OktaUserListItem): string {
    if (!user.mfa?.factors?.length) return '';
    return user.mfa.factors
      .map((f) => `${f.provider}: ${f.type} (${f.status})`)
      .join('\n');
  }

  canSuspend(status: OktaUserStatus): boolean {
    return !['SUSPENDED', 'DEPROVISIONED'].includes(status);
  }

  canResendInvite(user: OktaUserListItem): boolean {
    return (
      user.authProvider !== 'FEDERATION' &&
      ['PROVISIONED', 'STAGED'].includes(user.status)
    );
  }

  canResendCustomActivation(user: OktaUserListItem): boolean {
    return this.canResendInvite(user);
  }

  isResendingCustomActivation(user: OktaUserListItem): boolean {
    return this.resendingCustomActivationIds().has(user.id);
  }

  canResetPassword(user: OktaUserListItem): boolean {
    // TODO: Restore when reset-password permission is approved.
    // return (
    //   user.authProvider !== 'FEDERATION' &&
    //   ['ACTIVE', 'PASSWORD_EXPIRED'].includes(user.status)
    // );
    return false;
  }

  canUnlock(user: OktaUserListItem): boolean {
    return (
      user.authProvider !== 'FEDERATION' &&
      ['LOCKED_OUT', 'RECOVERY'].includes(user.status)
    );
  }

  canReactivate(user: OktaUserListItem): boolean {
    return (
      user.authProvider !== 'FEDERATION' &&
      ['SUSPENDED', 'DEPROVISIONED'].includes(user.status)
    );
  }

  canResetMfa(user: OktaUserListItem): boolean {
    return (
      user.authProvider !== 'FEDERATION' &&
      !!this.getResettableMfaFactorId(user)
    );
  }

  // --- Table actions ---

  resendInvite(user: OktaUserListItem): void {
    this.oktaUserService
      .resendActivationEmail(user.login)
      .pipe(
        takeUntil(this.destroy$),
        tap(() =>
          this.showToast('Invitation email resent successfully.', 'success'),
        ),
        catchError((err) => {
          this.showToast(
            err?.message ?? 'Failed to resend invitation.',
            'error',
          );
          return [];
        }),
      )
      .subscribe();
  }

  // TODO: Restore when reset-password permission is approved.
  // resetPassword(user: OktaUserListItem): void {
  //   this.oktaUserService
  //     .forgotPassword(user.login)
  //     .pipe(
  //       takeUntil(this.destroy$),
  //       tap(() =>
  //         this.showToast('Password reset email sent successfully.', 'success'),
  //       ),
  //       catchError((err) => {
  //         this.showToast(
  //           err?.message ?? 'Failed to send password reset.',
  //           'error',
  //         );
  //         return [];
  //       }),
  //     )
  //     .subscribe();
  // }

  unlockUser(user: OktaUserListItem): void {
    this.oktaUserService
      .unlockUser(user.login)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.showToast('User unlocked successfully.', 'success');
          this.patchUserInTable(user.id, {
            status: 'ACTIVE',
            cannotLogin: false,
            loginIssue: undefined,
          });
        }),
        catchError((err) => {
          this.showToast(err?.message ?? 'Failed to unlock user.', 'error');
          return [];
        }),
      )
      .subscribe();
  }

  resetMfa(user: OktaUserListItem): void {
    const factorId = this.getResettableMfaFactorId(user);

    if (!factorId) return;

    this.oktaUserService
      .resetMfaFactor(user.login, factorId)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.showToast('MFA factor reset successfully.', 'success');
          this.store.dispatch(
            OktaUsersActions.userMfaInvalidated({ userId: user.id }),
          );
          this.loadMfa(user);
        }),
        catchError((err) => {
          this.showToast(err?.message ?? 'Failed to reset MFA.', 'error');
          return [];
        }),
      )
      .subscribe();
  }

  deactivateOktaUser(user: OktaUserListItem): void {
    this.oktaUserService
      .deactivateUser({ login: user.login })
      .pipe(
        takeUntil(this.destroy$),
        tap((res) => {
          if (res.code === 'USER_REMOVED_FROM_GROUP') {
            // Federated users stay ACTIVE in Okta; they just leave the portal
            // group, so they drop out of the table and the lookup is refreshed.
            this.showToast(
              res.message || 'User removed from portal group.',
              'success',
            );
            this.store.dispatch(
              OktaUsersActions.userRemoved({ userId: user.id }),
            );
            if (this.lookupUserBase()?.id === user.id)
              this.lookUpUser(user.login);
            return;
          }
          this.showToast('User deactivated successfully.', 'success');
          this.patchUserInTable(user.id, {
            status: 'DEPROVISIONED',
            cannotLogin: true,
          });
        }),
        catchError((err) => {
          this.showToast(err?.message ?? 'Failed to deactivate user.', 'error');
          return [];
        }),
      )
      .subscribe();
  }

  suspendUser(user: OktaUserListItem): void {
    this.oktaUserService
      .deleteUser(user.login)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.showToast('User deleted successfully.', 'success');
          this.store.dispatch(
            OktaUsersActions.userRemoved({ userId: user.id }),
          );
          // Re-run the lookup so it reports the user as no longer in Okta.
          if (this.lookupUserBase()?.id === user.id)
            this.lookUpUser(user.login);
        }),
        catchError((err) => {
          this.showToast(err?.message ?? 'Failed to delete user.', 'error');
          return [];
        }),
      )
      .subscribe();
  }

  reactivateUser(user: OktaUserListItem): void {
    this.oktaUserService
      .resendActivationEmail(user.login)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.showToast(
            'User reactivated — activation email sent.',
            'success',
          );
          this.patchUserInTable(user.id, {
            status: 'PROVISIONED',
            cannotLogin: false,
          });
        }),
        catchError((err) => {
          this.showToast(err?.message ?? 'Failed to reactivate user.', 'error');
          return [];
        }),
      )
      .subscribe();
  }

  resendCustomActivation(user: OktaUserListItem): void {
    if (this.isResendingCustomActivation(user)) return;
    this.setResendingCustomActivation(user.id, true);

    this.oktaUserService
      .resendCustomActivationEmail(user.login)
      .pipe(
        takeUntil(this.destroy$),
        tap((res) => {
          if (res.code === 'ALREADY_ACTIVE') {
            // Nothing was sent; the user needs a password reset instead.
            this.showToast(res.message || 'User is already active.', 'error');
            this.patchUserInTable(user.id, { status: 'ACTIVE' });
            return;
          }
          // ACCOUNT_REACTIVATED_EMAIL_SENT means Okta's default email was
          // sent as a fallback; the API message explains that.
          this.showToast(
            res.message || 'Branded activation email sent.',
            'success',
          );
        }),
        catchError((err) => {
          this.showToast(
            err?.message ?? 'Failed to resend branded activation email.',
            'error',
          );
          return [];
        }),
        finalize(() => this.setResendingCustomActivation(user.id, false)),
      )
      .subscribe();
  }

  private setResendingCustomActivation(id: string, pending: boolean): void {
    this.resendingCustomActivationIds.update((ids) => {
      const next = new Set(ids);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  private patchUserInTable(id: string, patch: Partial<OktaUserListItem>): void {
    this.lookupUserBase.update((user) =>
      user?.id === id ? { ...user, ...patch } : user,
    );
    // Also drops the user's cached MFA so stale status isn't shown.
    this.store.dispatch(
      OktaUsersActions.userUpdated({ userId: id, changes: patch }),
    );
  }

  /**
   * Maps the raw Okta user from the exists lookup. The response is the source
   * of truth; a loaded table row only fills in fields it lacks, and its login
   * issue is kept only while the status still matches.
   */
  private toLookupUser(exists: OktaUserExistsData): OktaUserListItem | null {
    const oktaUser = exists.oktaUser;
    if (!exists.existsInOkta || !oktaUser?.id) return null;

    const row = this.dataSource.data.find((user) => user.id === oktaUser.id);
    const profile = oktaUser.profile ?? {};
    const status: OktaUserStatus =
      exists.status ?? oktaUser.status ?? row?.status;
    const rowIssueApplies = !!row && row.status === status;
    const pick = <T>(fresh: T | undefined, fallback: T): T =>
      fresh !== undefined ? fresh : fallback;

    return {
      id: oktaUser.id,
      login: profile.login ?? row?.login ?? exists.username,
      firstName: profile.firstName ?? row?.firstName ?? '',
      lastName: profile.lastName ?? row?.lastName ?? '',
      email: profile.email ?? row?.email ?? '',
      status,
      created: pick(oktaUser.created, row?.created ?? null),
      statusChanged: pick(oktaUser.statusChanged, row?.statusChanged ?? null),
      lastLogin: pick(oktaUser.lastLogin, row?.lastLogin ?? null),
      passwordChanged: pick(
        oktaUser.passwordChanged,
        row?.passwordChanged ?? null,
      ),
      authProvider: exists.authProvider
        ? exists.authProvider === 'FEDERATION'
          ? 'FEDERATION'
          : 'OKTA'
        : (row?.authProvider ?? 'OKTA'),
      profile: {
        userType: profile.userType ?? row?.profile?.userType ?? null,
      },
      cannotLogin: rowIssueApplies ? row.cannotLogin : false,
      loginIssue: rowIssueApplies ? row.loginIssue : undefined,
    };
  }

  private getResettableMfaFactorId(user: OktaUserListItem): string | null {
    return user.mfa?.enrolled
      ? (user.mfa.factors.find((factor) => !!factor.id)?.id ?? null)
      : null;
  }

  // --- Paging / MFA loading ---

  loadMore(): void {
    const cursors = this.loadedCursors$.value;
    if (this.loadMoreError()) {
      // Retry the page that failed rather than skipping past it.
      this.store.dispatch(
        OktaUsersActions.loadPage({ after: cursors[cursors.length - 1] }),
      );
      return;
    }

    const nextCursor = this.nextCursor;
    if (!nextCursor || this.isLoadingMore()) return;

    this.loadedCursors$.next([...cursors, nextCursor]);
    this.store.dispatch(OktaUsersActions.loadPage({ after: nextCursor }));
  }

  loadMfa(user: OktaUserListItem): void {
    this.store.dispatch(
      OktaUsersActions.loadUserMfa({ userId: user.id, login: user.login }),
    );
  }

  isMfaLoading(user: OktaUserListItem): boolean {
    return !!this.mfaStatus()[user.id]?.loading;
  }

  mfaError(user: OktaUserListItem): string | null {
    return this.mfaStatus()[user.id]?.error ?? null;
  }

  private loadUsers(): void {
    this.loadedCursors$.next([null]);
    this.store.dispatch(OktaUsersActions.loadPage({ after: null }));
  }

  /** Drops cached pages (e.g. a user was added/removed) and reloads page 1. */
  private reloadUsers(): void {
    this.store.dispatch(OktaUsersActions.pagesInvalidated());
    this.loadUsers();
  }

  private syncTable(cursors: (string | null)[], state: OktaUsersState): void {
    const now = Date.now();
    const firstKey = pageKey(cursors[0]);
    const lastKey = pageKey(cursors[cursors.length - 1]);
    const firstPage = state.pages[firstKey];
    const firstStatus = state.pageStatus[firstKey];
    const lastPage = state.pages[lastKey];
    const lastStatus = state.pageStatus[lastKey];
    const isLoadingMoreCursor = cursors.length > 1;

    // A cached first page is shown while it's refetched in the background.
    this.isLoading.set(!firstPage && !firstStatus?.error);
    this.errorMessage.set(firstPage ? null : (firstStatus?.error ?? null));
    this.isLoadingMore.set(isLoadingMoreCursor && !!lastStatus?.loading);
    this.loadMoreError.set(
      isLoadingMoreCursor && !lastPage ? (lastStatus?.error ?? null) : null,
    );
    this.nextCursor = lastPage?.nextCursor ?? null;
    this.hasMore.set(!!this.nextCursor);
    this.mfaStatus.set(state.mfaStatus);
    this.mfaCache.set(state.mfaByUserId);

    this.dataSource.data = cursors
      .flatMap((cursor) => state.pages[pageKey(cursor)]?.users ?? [])
      .map((user) => {
        const cached = state.mfaByUserId[user.id];
        return isFresh(cached, now) ? { ...user, mfa: cached.mfa } : user;
      });
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    this.snackBar.openFromComponent(ToastNotificationComponent, {
      duration: 7000,
      panelClass: ['toast-panel'],
      data: { message, type },
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
